import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { getProfileMe, updateBookingSettings, updateRequestSchedule } from '../../services/profileService';
import { ProfileData } from '../../types';
import Loading from '../../components/ui/Loading/Loading';
import { RequestScheduleModal, RequestScheduleModalRef } from '../../components/EditProfessional/RequestScheduleModal';
import { UnsavedChangesModal } from '../../components/EditProfileProfessional/UnsavedChangesModal/UnsavedChangesModal';
import { useNotification } from '../../context/NotificationContext';
import { AlertSnackbar } from '../../components/ui/AlertSnackbar';
import { useProfileCompletion } from '../../context/ProfileCompletionContext';
import './AvailabilityConfigurationPage.scss';

export const AvailabilityConfigurationPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { checkCompletionStatus } = useProfileCompletion();
    
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [bookingMode, setBookingMode] = useState<'direct' | 'request'>('request');
    const [messagingEnabled, setMessagingEnabled] = useState(true);
    
    // Initial tracking for change detection
    const [initialBookingMode, setInitialBookingMode] = useState<'direct' | 'request'>('request');
    const [initialMessagingEnabled, setInitialMessagingEnabled] = useState(true);
    
    const [isEditingRequestSchedule, setIsEditingRequestSchedule] = useState(false);
    const [requestScheduleData, setRequestScheduleData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [isSavedPulse, setIsSavedPulse] = useState(false);
    const [lastSaved, setLastSaved] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    const scheduleRef = useRef<RequestScheduleModalRef>(null);
    const [scheduleDirty, setScheduleDirty] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setIsEditMode(false);
            const data = await getProfileMe();
            const mode = (data?.details?.booking_mode as 'direct' | 'request') || 'request';
            setBookingMode(mode);
            setInitialBookingMode(mode);
            
            const messaging = data?.details?.messaging_enabled !== undefined 
                ? ['1', 1, 'true', true].includes(data.details.messaging_enabled) 
                : true;
            setMessagingEnabled(messaging);
            setInitialMessagingEnabled(messaging);
            setLastSaved(false);
            
            if (data?.details?.request_schedule) {
                try {
                    const schedule = typeof data.details.request_schedule === 'string' 
                        ? JSON.parse(data.details.request_schedule) 
                        : data.details.request_schedule;
                    setRequestScheduleData(schedule);
                } catch (e) {
                    console.error("Error parsing request_schedule:", e);
                }
            }
        } catch (err) {
            console.error('Error loading profile:', err);
            setError(t('appAdmin.profile.loadError', 'Error al cargar el perfil'));
        } finally {
            setLoading(false);
        }
    };

    const isDirty = bookingMode !== initialBookingMode || messagingEnabled !== initialMessagingEnabled;
    const isDirtyEffective = isEditingRequestSchedule ? scheduleDirty : isDirty;

    const saveBarTitle = (isSavedPulse || lastSaved) && !isDirtyEffective
        ? t('appAdmin.EditProfessionalInfo.changesSaved', 'Cambios guardados')
        : isDirtyEffective
            ? t('appAdmin.EditProfessionalInfo.savePrompt', 'Tenés cambios sin guardar')
            : t('appAdmin.EditProfessionalInfo.editing', 'Editando');

    const saveBarSubtitle = (isSavedPulse || lastSaved) && !isDirtyEffective
        ? t('appAdmin.EditProfessionalInfo.profileUpdated', 'Tu perfil está actualizado.')
        : isDirtyEffective
            ? t('appAdmin.EditProfessionalInfo.saveSubtitle', 'Guardalos para actualizar la información visible en tu perfil.')
            : t('appAdmin.EditProfessionalInfo.editingSubtitle', 'Modificá los campos que necesites y luego guardá los cambios.');

    const handleSave = async (andExit = false) => {
        if (isEditingRequestSchedule) {
            try {
                setSubmitting(true);
                if (scheduleRef.current) {
                    await scheduleRef.current.save();
                }
                setLastSaved(true);
                setIsSavedPulse(true);
                setTimeout(() => setIsSavedPulse(false), 2500);
                if (andExit) {
                    setIsEditingRequestSchedule(false);
                }
            } catch (err) {
                console.error('Error saving schedule:', err);
            } finally {
                setSubmitting(false);
            }
            return;
        }

        try {
            setSubmitting(true);
            await updateBookingSettings(bookingMode, messagingEnabled);
            setInitialBookingMode(bookingMode);
            setInitialMessagingEnabled(messagingEnabled);
            setLastSaved(true);
            setIsSavedPulse(true);
            setTimeout(() => setIsSavedPulse(false), 2500);
            await checkCompletionStatus();
            showNotification(t('appAdmin.profile.updateSuccess', 'Configuración guardada exitosamente'), 'success');
            if (andExit) {
                setIsEditMode(false);
            }
        } catch (err) {
            console.error('Error updating booking settings:', err);
            showNotification(t('appAdmin.profile.updateError', 'Error al guardar la configuración'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleExit = () => {
        if (isEditingRequestSchedule) {
            if (scheduleDirty) {
                setShowUnsavedModal(true);
            } else {
                setIsEditingRequestSchedule(false);
            }
            return;
        }

        if (isDirty) {
            setShowUnsavedModal(true);
        } else {
            setIsEditMode(false);
        }
    };

    const handleSaveRequestSchedule = async (scheduleData: any) => {
        try {
            await updateRequestSchedule(scheduleData);
            setRequestScheduleData(scheduleData);
            await checkCompletionStatus();
            showNotification(t('appAdmin.profile.updateSuccess', 'Horarios guardados exitosamente'), 'success');
        } catch (err) {
            console.error('Error saving request schedule:', err);
            showNotification(t('appAdmin.profile.updateError', 'Error al guardar horarios'), 'error');
            throw err;
        }
    };

    const getFormattedScheduleSummary = () => {
        if (!requestScheduleData) {
            return 'Lun, mié–vie 09:00–18:00 · Mar 13:00–18:00 · Sáb 09:00–12:00';
        }
        try {
            const daysMap: { [key: number]: string } = {
                1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 0: 'Dom'
            };
            const order = [1, 2, 3, 4, 5, 6, 0];

            if (requestScheduleData.type === 'block') {
                const sortedDays = (requestScheduleData.days || []).slice().sort((a: number, b: number) => {
                    return order.indexOf(a) - order.indexOf(b);
                });
                if (sortedDays.length === 0) return 'Sin días configurados';
                const parts: string[] = [];
                let s = 0;
                while (s < sortedDays.length) {
                    let e = s;
                    while (e + 1 < sortedDays.length && order.indexOf(sortedDays[e + 1]) === order.indexOf(sortedDays[e]) + 1) {
                        e += 1;
                    }
                    const firstLabel = daysMap[sortedDays[s]] || '';
                    const lastLabel = daysMap[sortedDays[e]] || '';
                    const length = e - s + 1;
                    if (length >= 3) parts.push(`${firstLabel}–${lastLabel}`);
                    else if (length === 2) parts.push(`${firstLabel} y ${lastLabel}`);
                    else parts.push(firstLabel);
                    s = e + 1;
                }
                return `${parts.join(', ')} ${requestScheduleData.start || '09:00'}–${requestScheduleData.end || '18:00'}`;
            }

            if (requestScheduleData.type === 'day' && requestScheduleData.schedule) {
                const daySchedule = requestScheduleData.schedule;
                const groups = new Map<string, number[]>();
                order.forEach(dayValue => {
                    const config = daySchedule[dayValue];
                    if (!config || !config.active) return;
                    const key = `${config.start}|${config.end}`;
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key)!.push(dayValue);
                });
                if (groups.size === 0) return 'Sin días configurados';
                const summaries = Array.from(groups.entries()).map(([key, dayValues]) => {
                    const [start, end] = key.split('|');
                    const parts: string[] = [];
                    let s = 0;
                    while (s < dayValues.length) {
                        let e = s;
                        while (e + 1 < dayValues.length && order.indexOf(dayValues[e + 1]) === order.indexOf(dayValues[e]) + 1) {
                            e += 1;
                        }
                        const firstLabel = daysMap[dayValues[s]] || '';
                        const lastLabel = daysMap[dayValues[e]] || '';
                        const length = e - s + 1;
                        if (length >= 3) parts.push(`${firstLabel}–${lastLabel}`);
                        else if (length === 2) parts.push(`${firstLabel} y ${lastLabel}`);
                        else parts.push(firstLabel);
                        s = e + 1;
                    }
                    return `${parts.join(', ')} ${start}–${end}`;
                });
                return summaries.join(' · ');
            }

            if (Array.isArray(requestScheduleData)) {
                const groups = new Map<string, number[]>();
                order.forEach(dayValue => {
                    const item = requestScheduleData.find((d: any) => Number(d.day) === dayValue);
                    if (!item || !item.enabled) return;
                    const key = `${item.from}|${item.to}`;
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key)!.push(dayValue);
                });
                if (groups.size === 0) return 'Sin días configurados';
                const summaries = Array.from(groups.entries()).map(([key, dayValues]) => {
                    const [from, to] = key.split('|');
                    const parts: string[] = [];
                    let s = 0;
                    while (s < dayValues.length) {
                        let e = s;
                        while (e + 1 < dayValues.length && order.indexOf(dayValues[e + 1]) === order.indexOf(dayValues[e]) + 1) {
                            e += 1;
                        }
                        const firstLabel = daysMap[dayValues[s]] || '';
                        const lastLabel = daysMap[dayValues[e]] || '';
                        const length = e - s + 1;
                        if (length >= 3) parts.push(`${firstLabel}–${lastLabel}`);
                        else if (length === 2) parts.push(`${firstLabel} y ${lastLabel}`);
                        else parts.push(firstLabel);
                        s = e + 1;
                    }
                    return `${parts.join(', ')} ${from}–${to}`;
                });
                return summaries.join(' · ');
            }
        } catch (e) {
            console.error("Error formatting summary:", e);
        }
        return 'Lun, mié–vie 09:00–18:00 · Mar 13:00–18:00 · Sáb 09:00–12:00';
    };

    if (loading) {
        return <Loading fullscreen text={t('appAdmin.AvailabilityConfiguration.loading', 'Cargando configuración...')} />;
    }

    const guideContent = bookingMode === 'request' ? {
        title: t('appAdmin.AvailabilityConfiguration.requestTitle', 'Solicitud de horario'),
        lead: t('appAdmin.AvailabilityConfiguration.requestLead', 'Definí los días y rangos en los que querés recibir propuestas. La hora exacta la confirmás vos.'),
        points: [
            { num: '1', title: t('appAdmin.AvailabilityConfiguration.requestPoint1Title', 'Configurá tus días'), text: t('appAdmin.AvailabilityConfiguration.requestPoint1Text', 'Podés aplicar un mismo horario a varios días o ajustar cada uno por separado.') },
            { num: '2', title: t('appAdmin.AvailabilityConfiguration.requestPoint2Title', 'El consultante propone'), text: t('appAdmin.AvailabilityConfiguration.requestPoint2Text', 'Elige día, franja y modalidad sin reservar una hora exacta.') },
            { num: '3', title: t('appAdmin.AvailabilityConfiguration.requestPoint3Title', 'Vos confirmás'), text: t('appAdmin.AvailabilityConfiguration.requestPoint3Text', 'Coordinás la hora definitiva o enviás una alternativa.') }
        ],
        noteTitle: t('appAdmin.AvailabilityConfiguration.valuesTitle', 'Valores y duración'),
        noteText: t('appAdmin.AvailabilityConfiguration.valuesText', 'Se toman de la configuración de tu Perfil público; no hace falta volver a cargarlos acá.')
    } : {
        title: t('appAdmin.AvailabilityConfiguration.directTitle', 'Reserva directa'),
        lead: t('appAdmin.AvailabilityConfiguration.directLead', 'Publicá franjas concretas para que el consultante pueda elegir y reservar un horario disponible.'),
        points: [
            { num: '1', title: t('appAdmin.AvailabilityConfiguration.directPoint1Title', 'Configurá tu agenda'), text: t('appAdmin.AvailabilityConfiguration.directPoint1Text', 'Definí tu disponibilidad habitual y realizá ajustes por fecha cuando lo necesites.') },
            { num: '2', title: t('appAdmin.AvailabilityConfiguration.directPoint2Title', 'Bodhiwell calcula los turnos'), text: t('appAdmin.AvailabilityConfiguration.directPoint2Text', 'Usa la duración de cada consulta y el tiempo entre consultas para ofrecer horarios válidos.') },
            { num: '3', title: t('appAdmin.AvailabilityConfiguration.directPoint3Title', 'Mantené la agenda al día'), text: t('appAdmin.AvailabilityConfiguration.directPoint3Text', 'Editá las franjas disponibles o realizá un ajuste puntual para una fecha concreta.') }
        ],
        noteTitle: t('appAdmin.AvailabilityConfiguration.valuesTitle', 'Valores y duración'),
        noteText: t('appAdmin.AvailabilityConfiguration.valuesText', 'Se toman de la configuración de tu Perfil público; no hace falta volver a cargarlos acá.')
    };

    return (
        <>
            <AlertSnackbar
                open={!!error}
                message={error || ''}
                onClose={() => setError(null)}
                severity="error"
            />
            
            <div className="professional-availability-config">
                <div className="availability-workspace">
                    <div className="availability-main-col">
                        {!isEditingRequestSchedule && (
                            <div className="profile-actions-sticky">
                                <div className="profile-actions" aria-label={t('appAdmin.AvailabilityConfiguration.actionsAriaLabel', 'Acciones de edición de disponibilidad')}>
                                    <button 
                                        className="btn-edit"
                                        id="btn-edit-availability"
                                        disabled={isEditMode}
                                        aria-disabled={isEditMode ? "true" : "false"}
                                        type="button"
                                        onClick={() => !isEditMode && setIsEditMode(true)}
                                    >
                                        {isEditMode 
                                            ? t('appAdmin.EditProfessionalInfo.editing', 'Editando') 
                                            : t('appAdmin.AvailabilityConfiguration.editAvailability', 'Editar disponibilidad')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <section aria-labelledby="availability-settings-title" className="availability-settings">
                            {!isEditingRequestSchedule ? (
                                <div className="availability-overview" id="availability-overview">
                                    <h3 className="availability-settings-title" id="availability-settings-title">{t('appAdmin.AvailabilityConfiguration.mainTitle', 'Disponibilidad')}</h3>
                                    <p className="availability-settings-intro">{t('appAdmin.AvailabilityConfiguration.mainIntro', 'Elegí cómo querés recibir solicitudes y reservas de consulta.')}</p>
                                    
                                    <fieldset className="availability-fieldset">
                                        <legend className="availability-legend">{t('appAdmin.AvailabilityConfiguration.formLegend', 'MODALIDAD DE DISPONIBILIDAD')}</legend>
                                        <div className="availability-options">
                                            <div className={`availability-option ${bookingMode === 'direct' ? 'is-selected' : ''} ${!isEditMode ? 'is-readonly' : ''}`}>
                                                <label className="availability-option-main">
                                                    <input 
                                                        name="availability-mode" 
                                                        type="radio" 
                                                        value="direct"
                                                        disabled={!isEditMode}
                                                        checked={bookingMode === 'direct'}
                                                        onChange={() => {
                                                            if (!isEditMode) return;
                                                            setBookingMode('direct');
                                                            setLastSaved(false);
                                                        }}
                                                    />
                                                    <span aria-hidden="true" className="availability-radio"></span>
                                                    <span className="availability-option-copy">
                                                        <strong>{t('appAdmin.AvailabilityConfiguration.directRadioTitle', 'Reserva directa')}</strong>
                                                        <span>{t('appAdmin.AvailabilityConfiguration.directRadioText', 'Definí días y horarios para que los consultantes puedan reservar directamente.')}</span>
                                                    </span>
                                                </label>
                                                
                                                {bookingMode === 'direct' && (
                                                    <div className="availability-option-detail direct-availability" id="direct-availability">
                                                        <div className="direct-availability-copy">
                                                            <span>{t('appAdmin.AvailabilityConfiguration.directDetailText', 'Administrá los días y horarios concretos que podrán reservarse directamente.')}</span>
                                                        </div>
                                                        <button 
                                                            className="direct-configure" 
                                                            type="button" 
                                                            disabled={!isEditMode}
                                                            aria-disabled={!isEditMode}
                                                            onClick={() => {
                                                                if (!isEditMode) return;
                                                                navigate('/availability/direct');
                                                            }}
                                                        >
                                                            {t('appAdmin.AvailabilityConfiguration.directConfigureBtn', 'Configurar agenda')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            <div className={`availability-option ${bookingMode === 'request' ? 'is-selected' : ''} ${!isEditMode ? 'is-readonly' : ''}`}>
                                                <label className="availability-option-main">
                                                    <input 
                                                        name="availability-mode" 
                                                        type="radio" 
                                                        value="request" 
                                                        disabled={!isEditMode}
                                                        checked={bookingMode === 'request'}
                                                        onChange={() => {
                                                            if (!isEditMode) return;
                                                            setBookingMode('request');
                                                            setLastSaved(false);
                                                        }}
                                                    />
                                                    <span aria-hidden="true" className="availability-radio"></span>
                                                    <span className="availability-option-copy">
                                                        <strong>{t('appAdmin.AvailabilityConfiguration.requestRadioTitle', 'Solicitud de horario')}</strong>
                                                        <span>{t('appAdmin.AvailabilityConfiguration.requestRadioText', 'El consultante propone un horario. Vos lo confirmás o sugerís una alternativa.')}</span>
                                                    </span>
                                                </label>
                                                
                                                {bookingMode === 'request' && (
                                                    <div className="availability-option-detail request-availability" id="request-availability" aria-labelledby="request-availability-title">
                                                        <div className="request-availability-copy">
                                                            <strong id="request-availability-title">{t('appAdmin.AvailabilityConfiguration.requestDaysExampleTitle', 'Días de atención · Ejemplo')}</strong>
                                                            <span id="request-schedule-summary">{getFormattedScheduleSummary()}</span>
                                                        </div>
                                                        <button 
                                                            className="request-configure" 
                                                            id="request-configure"
                                                            type="button" 
                                                            disabled={!isEditMode}
                                                            aria-disabled={!isEditMode}
                                                            onClick={() => {
                                                                if (!isEditMode) return;
                                                                setIsEditingRequestSchedule(true);
                                                            }}
                                                        >
                                                            {t('appAdmin.AvailabilityConfiguration.requestConfigureBtn', 'Configurar días')}
                                                        </button>
                                                        <p className="request-availability-note">
                                                            {t('appAdmin.AvailabilityConfiguration.requestDaysNote', 'Podés aplicar horarios por bloque o ajustar cada día. La hora definitiva la confirmás vos.')}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </fieldset>

                                    <div aria-hidden="true" className="availability-divider"></div>
                                    
                                    <div className="chat-block chat-highlight">
                                        <div className="chat-setting">
                                            <div className="chat-setting-main">
                                                <span aria-hidden="true" className="chat-icon">
                                                    <svg viewBox="0 0 24 24"><path d="M4 5.5h16v11H8l-4 3v-14Z"></path></svg>
                                                </span>
                                                <div className="chat-setting-copy">
                                                    <div className="chat-title-row">
                                                        <strong>{t('appAdmin.AvailabilityConfiguration.chatTitle', 'Chat con consultantes')}</strong>
                                                        <span className="chat-tag">{t('appAdmin.AvailabilityConfiguration.chatTag', 'Complementario')}</span>
                                                    </div>
                                                    <span>{t('appAdmin.AvailabilityConfiguration.chatDesc', 'Permití mensajes para aclarar dudas o terminar de coordinar detalles antes de reservar.')}</span>
                                                </div>
                                            </div>
                                            <label className={`switch ${!isEditMode ? 'is-disabled' : ''}`}>
                                                <input 
                                                    aria-label="Recibir mensajes por chat interno" 
                                                    type="checkbox"
                                                    disabled={!isEditMode}
                                                    checked={messagingEnabled}
                                                    onChange={(e) => {
                                                        if (!isEditMode) return;
                                                        setMessagingEnabled(e.target.checked);
                                                        setLastSaved(false);
                                                    }}
                                                />
                                                <span aria-hidden="true" className="switch-track">
                                                    <span className="switch-thumb"></span>
                                                </span>
                                            </label>
                                        </div>
                                        <p className="chat-benefit">
                                            <strong>{t('appAdmin.AvailabilityConfiguration.chatBenefitBold', 'Cuándo ayuda:')}</strong> {t('appAdmin.AvailabilityConfiguration.chatBenefitText', 'modalidad, preparación o coordinación. El chat acompaña el flujo; no reemplaza la reserva ni la solicitud de horario.')}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <RequestScheduleModal 
                                    ref={scheduleRef}
                                    isOpen={true}
                                    onClose={handleExit}
                                    onSave={handleSaveRequestSchedule}
                                    initialSchedule={requestScheduleData}
                                    hideCloseButton={true}
                                    onDirtyChange={setScheduleDirty}
                                />
                            )}
                        </section>

                        {(isEditMode || isEditingRequestSchedule) && (
                            <div aria-label="Acciones de guardado de disponibilidad" className="save-bar-wrap availability-shared-save bw-editor-save">
                                <div className="save-pill">
                                    <div className="save-state">
                                        <div 
                                            className={`save-dot ${((isSavedPulse || lastSaved) && !isDirtyEffective) ? 'is-saved' : ''}`}
                                            style={{ opacity: isDirtyEffective || ((isSavedPulse || lastSaved) && !isDirtyEffective) ? 1 : 0 }}
                                        />
                                        <div className="save-info">
                                            <div className="save-copy">{saveBarTitle}</div>
                                            <div className="save-sub">{saveBarSubtitle}</div>
                                        </div>
                                    </div>
                                    <div className="save-actions">
                                        <button 
                                            className="btn btn-save-exit" 
                                            id="btn-exit" 
                                            type="button"
                                            onClick={handleExit}
                                        >
                                            {t('appAdmin.EditProfessionalInfo.exit', 'Salir')}
                                        </button>
                                        <button 
                                            className={`btn save-cta ${isSavedPulse ? 'is-saved' : ''}`} 
                                            id="btn-save" 
                                            type="button"
                                            onClick={() => handleSave(false)}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <CircularProgress size={18} color="inherit" />
                                            ) : isSavedPulse ? (
                                                t('appAdmin.EditProfessionalInfo.saved', 'Guardado')
                                            ) : (
                                                t('appAdmin.EditProfessionalInfo.save', 'Guardar')
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <aside aria-label={t('appAdmin.AvailabilityConfiguration.guideAriaLabel', 'Ayuda para configurar disponibilidad')} className="availability-guide">
                        <div className="availability-guide-card">
                            <span className="availability-guide-kicker">{t('appAdmin.AvailabilityConfiguration.guideKicker', 'PARA ORIENTARTE')}</span>
                            <h3 className="availability-guide-title">{guideContent.title}</h3>
                            <p className="availability-guide-lead">{guideContent.lead}</p>
                            
                            <div className="availability-guide-points">
                                {guideContent.points.map((point) => (
                                    <div className="availability-guide-point" key={point.num}>
                                        <span>{point.num}</span>
                                        <div>
                                            <strong>{point.title}</strong>
                                            <p>{point.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="availability-guide-note">
                                <strong>{guideContent.noteTitle}</strong>
                                <span>{guideContent.noteText}</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <UnsavedChangesModal
                open={showUnsavedModal}
                description={isEditingRequestSchedule 
                    ? t('appAdmin.UnsavedChangesModal.scheduleDescription', 'Si salís ahora, los cambios realizados en Solicitud de horario se van a perder.')
                    : undefined
                }
                onClose={() => setShowUnsavedModal(false)}
                onExitWithoutSaving={() => {
                    setShowUnsavedModal(false);
                    if (isEditingRequestSchedule) {
                        scheduleRef.current?.reset();
                        setScheduleDirty(false);
                        setIsEditingRequestSchedule(false);
                    } else {
                        setBookingMode(initialBookingMode);
                        setMessagingEnabled(initialMessagingEnabled);
                        setIsEditMode(false);
                    }
                }}
                onSaveAndExit={async () => {
                    setShowUnsavedModal(false);
                    await handleSave(true);
                }}
            />
        </>
    );
};

export default AvailabilityConfigurationPage;
